import { colors, editorColorPalette, productColors } from 'theme/colors'

// Re-export the color palette for use in the editor toolbar
export const colorPalette = editorColorPalette

export const quillEditorStyle = {
  '.ql-editor': {
    // Normal text - Body/16 font
    fontFamily: `'Lato', sans-serif`,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '150%',
    color: productColors.text.dark.bodySolid,
    '& ul': {
      listStyleType: 'disc',
    },
    '& ol': {
      listStyleType: 'decimal',
    },
    '& li': {
      listStylePosition: 'inside',
      listStyleType: 'inherit',
    },
    // Heading 1 - Product/H3 font
    '& h1': {
      fontFamily: `'Lato', sans-serif`,
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: '120%',
      letterSpacing: '0.5px',
      color: colors.blueGray[700],
      marginTop: '18px',
      marginBottom: '6px',
    },
    '& h1:first-child': {
      marginTop: '0px',
      marginBottom: '6px',
    },
    // Heading 2 - Product/H4 font
    '& h2': {
      fontFamily: `'Lato', sans-serif`,
      fontSize: '23px',
      fontWeight: 700,
      lineHeight: '120%',
      letterSpacing: '0.5px',
      color: colors.blueGray[700],
      marginTop: '6px',
      marginBottom: '0px',
    },
    '& h2:first-child': {
      marginTop: '0px',
      marginBottom: '0px',
    },
    // Heading 3 - Product/H5 font
    '& h3': {
      fontFamily: `'Lato', sans-serif`,
      fontSize: '19px',
      fontWeight: 600,
      lineHeight: '150%',
      color: colors.blueGray[700],
      marginTop: '0px',
      marginBottom: '0px',
    },
    // Normal text (paragraphs)
    '& p': {
      marginTop: '0px',
      marginBottom: '0px',
    },
    // General spacing between elements (6px gap)
    '& > *:not(:first-child)': {
      marginTop: '6px',
    },
    // Override for h1 which has its own larger top margin
    '& > h1:not(:first-child)': {
      marginTop: '18px',
    },
    '& blockquote': {
      marginBottom: '8px',
      marginTop: '8px',
      paddingLeft: '16px',
      borderLeft: `4px solid ${productColors.border.solid}`,
      color: productColors.text.dark.bodySubtle,
      fontStyle: 'italic',
    },
    '& a': {
      color: productColors.cgBlue,
      textDecoration: 'underline',
      _hover: {
        color: colors.product.blue[700],
      },
    },
  },
}

// Custom styled wrapper for Quill editor
export const quillWrapperStyles = {
  // Make Quill fill the container
  '.quill': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  // Container styles
  '.ql-container': {
    borderColor: 'product.border.solid',
    borderBottomRadius: 'md',
    fontFamily: `'Lato', sans-serif`,
    flex: '1',
    overflow: 'visible',
  },
  // Link tooltip styles
  '.ql-tooltip.ql-editing': {
    boxShadow: 'none',
    borderRadius: '4px',
  },
  // Toolbar styles
  '.ql-toolbar': {
    borderColor: 'product.border.solid',
    borderTopRadius: 'md',
    flexShrink: 0,
    paddingLeft: '16px !important',
    paddingRight: '16px !important',
  },
  // Toolbar button styles
  '.ql-toolbar button': {
    width: '28px',
    height: '28px',
    padding: '4px',
  },
  '.ql-toolbar button:hover': {
    bg: 'gray.100',
    borderRadius: 'sm',
  },
  '.ql-toolbar button.ql-active': {
    bg: 'product.activeBg',
    borderRadius: 'sm',
  },
  // Icon colors - use !important to override Quill defaults
  '.ql-toolbar .ql-stroke': {
    stroke: `${colors.product.blue[900]} !important`,
  },
  '.ql-toolbar .ql-fill': {
    fill: `${colors.product.blue[900]} !important`,
  },
  '.ql-toolbar button:hover .ql-stroke': {
    stroke: `${colors.product.blue[900]} !important`,
  },
  '.ql-toolbar button:hover .ql-fill': {
    fill: `${colors.product.blue[900]} !important`,
  },
  '.ql-toolbar button.ql-active .ql-stroke': {
    stroke: `${colors.product.blue[900]} !important`,
  },
  '.ql-toolbar button.ql-active .ql-fill': {
    fill: `${colors.product.blue[900]} !important`,
  },
  // Picker styles - base (for all pickers)
  '.ql-toolbar .ql-picker': {
    color: `${productColors.text.dark.bodySolid} !important`,
  },
  '.ql-toolbar .ql-picker-label': {
    color: `${productColors.text.dark.bodySolid} !important`,
  },
  // Header picker - styled as button
  '.ql-toolbar .ql-picker.ql-header': {
    border: '1px solid',
    borderColor: 'product.border.solid',
    borderRadius: '4px',
    height: '26px',
  },
  '.ql-toolbar .ql-picker.ql-header .ql-picker-label': {
    borderColor: 'transparent',
    borderRadius: '4px',
    paddingLeft: '0',
    paddingRight: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  '.ql-toolbar .ql-picker.ql-header .ql-picker-label:hover': {
    color: `${productColors.text.dark.bodySolid} !important`,
    bg: 'gray.50',
  },
  '.ql-toolbar .ql-picker.ql-header.ql-expanded .ql-picker-label': {
    borderColor: 'product.primary',
    bg: 'white',
  },
  // Align and Color pickers - styled as icons only
  '.ql-toolbar .ql-picker.ql-align, .ql-toolbar .ql-picker.ql-color': {
    border: 'none',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
  },
  '.ql-toolbar .ql-picker.ql-align .ql-picker-label, .ql-toolbar .ql-picker.ql-color .ql-picker-label':
    {
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    },
  '.ql-toolbar .ql-picker.ql-align .ql-picker-label:hover, .ql-toolbar .ql-picker.ql-color .ql-picker-label:hover':
    {
      bg: 'gray.100',
      borderRadius: 'sm',
    },
  '.ql-toolbar .ql-picker-options': {
    borderColor: 'product.border.solid',
    borderRadius: '4px',
    boxShadow: 'md',
    padding: '4px',
    marginTop: '4px',
  },
  '.ql-toolbar .ql-picker-item': {
    color: `${productColors.text.dark.bodySolid} !important`,
    borderRadius: 'md',
    paddingX: '8px',
    paddingY: '4px',
  },
  '.ql-toolbar .ql-picker-item:hover': {
    bg: 'gray.50',
    color: `${productColors.text.dark.bodySolid} !important`,
  },
  // Editor area
  '.ql-editor': {
    height: '100%',
  },
  '.ql-editor.ql-blank::before': {
    color: 'product.text.dark.placeholder',
    fontStyle: 'normal',
  },
}
